import {mapGetters} from 'vuex'
import _ from 'lodash'
import {BUSINESS_TIMEZONE, MARKET_CLOSE_MST_HOUR, MarkupTypes, UNITED_STATES} from '@/util/constants'
import {default as productMixin} from '@/mixins/product'
import {DateTime} from 'luxon-business-days'
import {isPreSettlementPaymentMethod, titleModality} from '@/util/preSettlement'

// ─── US Federal Holiday helpers ─────────────────────────────────────────────────
// Mirrors platformgold-api/models/primary/sales-orders/constants.js to maintain
// client-server parity for shipping hold date validation.

/**
 * Return the observed date for a fixed US federal holiday.
 * If the holiday falls on Saturday the preceding Friday is observed;
 * if it falls on Sunday the following Monday is observed.
 */
function observedDate(dt) {
  if (dt.weekday === 6) return dt.minus({days: 1}) // Saturday → Friday
  if (dt.weekday === 7) return dt.plus({days: 1})   // Sunday → Monday
  return dt
}

/**
 * Return the nth occurrence of a given weekday in the specified month/year.
 * @param {number} year
 * @param {number} month      1–12
 * @param {number} dayOfWeek  Luxon weekday (1 = Monday … 7 = Sunday)
 * @param {number} n          1-based occurrence
 */
function nthWeekdayOfMonth(year, month, dayOfWeek, n) {
  let dt = DateTime.fromObject({year, month, day: 1})
  while (dt.weekday !== dayOfWeek) dt = dt.plus({days: 1})
  return dt.plus({weeks: n - 1})
}

/**
 * Return the last occurrence of a given weekday in the specified month/year.
 */
function lastWeekdayOfMonth(year, month, dayOfWeek) {
  let dt = DateTime.fromObject({year, month}).endOf('month').startOf('day')
  while (dt.weekday !== dayOfWeek) dt = dt.minus({days: 1})
  return dt
}

/**
 * Compute US federal holidays (observed dates) for a given year.
 * Returns an array of ISO date strings (YYYY-MM-DD).
 *
 * Per the platform terms of service, business days exclude US federal holidays.
 * This list must match getUSFederalHolidays in the server-side sales-orders constants.
 *
 * @param {number} year
 * @returns {string[]}
 */
function getUSFederalHolidays(year) {
  return [
    observedDate(DateTime.fromObject({year, month: 1, day: 1})),    // New Year's Day
    nthWeekdayOfMonth(year, 1, 1, 3),                               // MLK Day (3rd Mon in Jan)
    nthWeekdayOfMonth(year, 2, 1, 3),                               // Presidents' Day (3rd Mon in Feb)
    lastWeekdayOfMonth(year, 5, 1),                                  // Memorial Day (last Mon in May)
    observedDate(DateTime.fromObject({year, month: 6, day: 19})),   // Juneteenth
    observedDate(DateTime.fromObject({year, month: 7, day: 4})),    // Independence Day
    nthWeekdayOfMonth(year, 9, 1, 1),                               // Labor Day (1st Mon in Sep)
    nthWeekdayOfMonth(year, 10, 1, 2),                              // Columbus Day (2nd Mon in Oct)
    observedDate(DateTime.fromObject({year, month: 11, day: 11})),  // Veterans Day
    nthWeekdayOfMonth(year, 11, 4, 4),                              // Thanksgiving (4th Thu in Nov)
    observedDate(DateTime.fromObject({year, month: 12, day: 25})),  // Christmas Day
  ].map(dt => dt.toISODate())
}

export default {
  data() {
    return {
      NGI_NETSUITE_INTERNAL_ID: `618802`,
      LEGACY_DEPO_ATTENTION_DELIM: ' ACCT #',
      DEPO_ATTENTION_DELIM: ' #',
      HOLD_FOR_INSTRUCTIONS_NS_ID: 5,
      DEPOSITORY_INSTRUCTIONS_NS_ID: 2,
      CONFIDENTIAL_DROPSHIP_NS_ID: 3,
      ADDRESS_CHAR_LIMIT: 30,
      requireObjectField: [
        (v) => {
          return (
            v === null ||
            v === undefined ||
            this.partialValidation ||
            v.id !== null ||
            'This field is required'
          )
        },
      ],
      noShippingAddressRequired: {
        tbd: true,
        location: {
          addressee: 'TBD',
          label: 'NO SHIPPING ADDRESS REQUIRED',
        },
      },
      requireDepoAddress:(v) => {
       {return Boolean(v.street1) || 'Depository selection required'}
      },
    }
  },
  computed: {
    ...mapGetters([
      'getSelectedCompany',
      'getSelectedCompanyAddresses',
      'getPaymentMethodsByBuySell',
      'getDefaultPaymentMethodsByBuySell',
    ]),
  },
  methods: {
    nextMarketCloseRaw() {
      return this.now().setZone('America/Denver').set({
        hour: MARKET_CLOSE_MST_HOUR,
        minute: 0,
        second: 0,
        millisecond: 0,
      })
    },
    nextMarketClose() {
      let nextMarketClose = this.nextMarketCloseRaw()
      if (nextMarketClose < this.now())
        nextMarketClose = nextMarketClose.plusBusiness()
      return nextMarketClose.toFormat('yyyy-MM-dd')
    },
    now() {
      return DateTime.now()
    },
    /**
     * - Cannot be in the past (prior to refDate, subject to cut off time)
     * - Must be a business day (Mon–Fri, excluding US federal holidays)
     * - Cannot be more than 28 days in the future
     *
     * @returns {boolean}
     */
    allowedDates(val, days = 28, refDate = DateTime.local().startOf('day')) {
      let luxonDate = DateTime.fromISO(val)
      // if luxonDate is equal to refDate, check if it's before the cutoff time
      let dateIsTodayAndBeforeCutoff = false
      if (luxonDate.startOf('day').diff(refDate.startOf('day'), ['seconds']).seconds === 0) {
        const refDateNow = DateTime.now()
        dateIsTodayAndBeforeCutoff = refDateNow.hour < refDate.hour
      }
      // Exclude US federal holidays to maintain parity with server-side validation.
      // Also check the next year's holidays to handle the year-boundary edge case:
      // e.g. if Jan 1 falls on Saturday, the observed holiday is Dec 31 of the prior year,
      // which would only appear in getUSFederalHolidays(year + 1).
      const holidays = [
        ...getUSFederalHolidays(luxonDate.year),
        ...getUSFederalHolidays(luxonDate.year + 1),
      ]
      return (
        (dateIsTodayAndBeforeCutoff || luxonDate >= refDate) &&
        luxonDate.isBusinessDay() &&
        !holidays.includes(luxonDate.toISODate()) &&
        luxonDate < refDate.plus({days})
      )
    },
    minAchDebitDate(dates = []) {
      const format = "yyyy-MM-dd"
      return DateTime.max(...dates.map(dateStr => DateTime.fromFormat(dateStr, format))).toFormat(format)
    },
    async cancelLimitOrder(limitOrder, loader) {

      return new Promise((resolve) => {
        window._VMA.$emit('CONFIRM', {
          title: `Confirm Cancel`,
          message: `Are you sure you want to cancel this limit order?`,
          options: {},
          callback: (agree) => {
            if (agree) {
              loader.open(
                'Canceling Limit Order...',
                'Please do not navigate away from this window until your cancellation is complete.',
                {
                  color: 'primary',
                }
              )
              return this.$store
                .dispatch('cancelLimitOrder', {id: limitOrder.id})
                .then(() => {
                  window._VMA.$emit('SHOW_SNACKBAR', {
                    text: `Limit order #LO${limitOrder.confirmationNumber} has been successfully canceled.`,
                    color: 'green',
                  })
                  loader.close()
                  resolve()
                })
                .catch((err) => {
                  window._VMA.$emit('SHOW_SNACKBAR', {
                    text: err.response.data,
                    color: 'error',
                  })
                  loader.close()
                  resolve()
                })
            }
          },
        })
      })
    },
    async fillLimitOrder(limitOrder, loader) {
      return new Promise((resolve) => {
        const ask = limitOrder.product
          ? limitOrder.product.ask
          : limitOrder[`product.ask`]
        const currentAsk = ask * limitOrder.qty
        const fillAsk = limitOrder.limitPrice * limitOrder.qty
        const difference = currentAsk - fillAsk
        const formatted = this.$options.filters.toCurrency(Math.abs(difference))
        const noun = difference < 0 ? `profit` : `deficit`
        window._VMA.$emit('CONFIRM', {
          title: `Confirm Fill`,
          message: `Are you sure you want to force fill this order at a ${noun} of ${formatted}?`,
          options: {},
          callback: (agree) => {
            if (agree) {
              loader.open(
                'Filling Limit Order...',
                'Please do not navigate away from this window until your fill is complete.',
                {
                  color: 'primary',
                }
              )
              return this.$store
                .dispatch('fillLimitOrder', {id: limitOrder.id})
                .then((response) => {
                  this.$router.push(
                    `/orders/sales-orders/${response.netsuite_internalId}`
                  )
                  resolve()
                  loader.close()
                })
                .catch((err) => {
                  window._VMA.$emit('SHOW_SNACKBAR', {
                    text: err.response.data,
                    color: 'error',
                  })
                  resolve()
                  loader.close()
                })
            }
          },
        })
      })
    },
    getConfirmationNumber(prefix = `PG`, currentConfirmationNumber) {
      if (!currentConfirmationNumber)
        currentConfirmationNumber = `${prefix}${
          this.getSelectedCompany.netsuite_internalId
        }${Date.now().toString().substr(3)}${
          this.buySell === `buy` ? '1' : '0'
        }`
      return currentConfirmationNumber
    },
    getPaymentMethods(buySell) {
      const paymentMethods = this.getPaymentMethodsByBuySell(buySell).length > 0
        ? [...this.getPaymentMethodsByBuySell(buySell)].sort(this.alphaSort)
        : [...this.getDefaultPaymentMethodsByBuySell(buySell)].sort(
          this.alphaSort
        )
      return paymentMethods.map((paymentMethod) => {
        return {
          ...paymentMethod,
          title: this.formatPaymentMethodTitle(paymentMethod),
        }
      })
    },
    getPreSettlementTermIds() {
      const configuredIds = this.getConfig && this.getConfig('PRE_SETTLEMENT_TERM_IDS')
      const ids = Array.isArray(configuredIds)
        ? configuredIds
        : String(configuredIds || '').split(',')
      return new Set(ids.map((id) => String(id).trim()).filter(Boolean))
    },
    isPreSettlementPaymentMethod(paymentMethod) {
      return isPreSettlementPaymentMethod(paymentMethod, this.getPreSettlementTermIds())
    },
    formatPaymentMethodTitle(paymentMethod) {
      const title = String(_.get(paymentMethod, 'title', ''))
      if (this.isPreSettlementPaymentMethod(paymentMethod)) {
        const modality = titleModality(title)
        if (modality === 'wire') return 'Pre-Settlement - Wire Payment'
        if (modality === 'ach') return 'Pre-Settlement - ACH Payment'
        return 'Pre-Settlement Payment'
      }
      return title
    },
    formatOrderTermsTitle(terms) {
      if (!terms) return ''
      const sellMethods = this.getPaymentMethods('sell')
      const matched = sellMethods.find(
        (paymentMethod) => String(paymentMethod.netsuite_internalId) === String(terms.id)
      )
      if (matched) {
        return matched.title
      }
      return this.formatPaymentMethodTitle({
        netsuite_internalId: terms.id,
        title: terms.refName,
      })
    },
    fixNumber(num) {
      //DUPLICATE CODE
      const actualNumber =
        typeof num === `string` ? parseFloat(num.replace(`,`, ``)) : num
      return Math.round(actualNumber * 100) / 100
    },
    getMarkedUpRateForItem(item, orderRetail) {
      let rate = parseFloat(_.get(item, 'rate'))
      const handlingFeeMarkup = _.get(orderRetail, `handlingFeeMarkup`) || 0
      const handlingFeeMarkupType = _.get(orderRetail, `handlingFeeMarkupType`)
      if (
        handlingFeeMarkup &&
        parseInt(item.item.id) ===
        parseInt(orderRetail.config.financials.handling_fee_ns_id)
      ) {
        if (handlingFeeMarkupType === MarkupTypes.PERCENT)
          return this.fixNumber(
            rate * (1 + parseFloat(handlingFeeMarkup) / 100)
          )
        else return this.fixNumber(rate + parseFloat(handlingFeeMarkup))
      }
      const matchingLineItem = this.getMatchingLineItem(item, orderRetail)
      if (matchingLineItem && matchingLineItem.markupType) {
        if (matchingLineItem.markupType === MarkupTypes.PERCENT)
          rate = this.fixNumber(
            rate * (1 + parseFloat(matchingLineItem.markup) / 100)
          )
        else rate = this.fixNumber(rate + parseFloat(matchingLineItem.markup))
      }
      if (matchingLineItem && matchingLineItem.markupTypeRep) {
        if (matchingLineItem.markupTypeRep === MarkupTypes.PERCENT)
          rate = this.fixNumber(
            rate * (1 + parseFloat(matchingLineItem.markupRep) / 100)
          )
        else
          rate = this.fixNumber(rate + parseFloat(matchingLineItem.markupRep))
      }
      return this.fixNumber(rate)
    },
    getMarkupTextForItem(item, orderRetail, rep = false) {
      const keys = rep
        ? {type: `markupTypeRep`, markup: `markupRep`}
        : {type: `markupType`, markup: `markup`}
      const prefix = rep ? `Sales Rep Markup: ` : `Company Markup: `
      const matchingLineItem = this.getMatchingLineItem(item, orderRetail)
      if (
        matchingLineItem &&
        matchingLineItem[keys.type] &&
        matchingLineItem[keys.type] === MarkupTypes.PERCENT
      )
        return `${prefix}${this.fixNumber(matchingLineItem[keys.markup])}%`
      if (
        matchingLineItem &&
        matchingLineItem[keys.type] &&
        matchingLineItem[keys.type] === MarkupTypes.FLAT
      )
        return `${prefix}+${productMixin.methods.toCurrency(
          matchingLineItem[keys.markup]
        )}`
    },
    getMatchingLineItem(item, orderRetail) {
      const lineItems = orderRetail.salesOrderLineItem
        ? orderRetail.salesOrderLineItem
        : orderRetail.purchaseOrderLineItem
      const matchingLineItem = lineItems.filter(
        (x) =>
          parseInt(x.product.netsuite_internalId) === parseInt(item.item.id)
      )
      if (matchingLineItem.length > 0) return matchingLineItem[0]
    },
    characterLimit(limit) {
      return [
        (v) => {
          return (
            v === undefined ||
            v === null ||
            !v.length ||
            (v.length && v.length <= limit) ||
            `This field is limited to ${limit} characters`
          )
        },
      ]
    },
    getCustomerReferencePlaceholder(buySell) {
      return `Enter your trade number, customer name, or other reference to help you identify this order`
    },
    addressHint(address) {
      if (
        address &&
        Object.keys(address).length > 0 &&
        address.location &&
        address.id
      ) {
        return `${address.location.street1}${
          address.location.street2 ? ', ' + address.location.street2 : ''
        } ${address.location.city} ${address.location.state} ${
          address.location.zip
        }`
      } 
    },
    warehouseAddressHint(address) {
      if (
        address !== null &&
        Object.keys(address).length > 0 &&
        address.zip &&
        address.zip.length > 0
      ) {
        return `${address.street1}${
          address.street2 ? ', ' + address.street2 : ''
        } ${address.city} ${address.state} ${address.zip}`
      }
    },
    alphaSort(a, b) {
      if (a.title < b.title) {
        return -1
      }
      if (a.title > b.title) {
        return 1
      }
      return 0
    },
    getAvailableShippingAddresses(selectedShippingInstruction) {
      if (
        !_.isEmpty(selectedShippingInstruction) &&
        parseInt(selectedShippingInstruction.netsuite_internalId) ===
        this.HOLD_FOR_INSTRUCTIONS_NS_ID
      ) {
        return [this.noShippingAddressRequired]
      }
      return [..._.get(this.getSelectedCompanyAddresses, `data.rows`, [])].sort(this.alphaSort);
    },
    getMerchandiseProcessingFee(buySell, company, subtotal) {
      if (buySell !== `sell` || _.get(company, 'defaultBillingAddress.location.country') === UNITED_STATES) return null
      const minFee = 32.71
      const maxFee = 634.62
      const fee = subtotal * 0.003464
      return (fee > minFee ? (fee < maxFee ? fee : maxFee) : minFee) * -1
    },
    validateShippingAddressComplete(buySell, selectedShippingInstructionId) {
      return (v) => {
        if (selectedShippingInstructionId === this.HOLD_FOR_INSTRUCTIONS_NS_ID) return true
        if (buySell === `sell`) return true
        const instructions = (selectedShippingInstructionId === this.CONFIDENTIAL_DROPSHIP_NS_ID) ? 'Please select Hold for Instructions instead.' : ''
        const {location} = v || {}
        const requiredFields = {
          street1: 'Street Address',
          city: 'City',
          state: 'State',
          zip: 'ZIP Code',
          country: 'Country',
        }
        const missing = Object.entries(requiredFields)
          .filter(([key]) => !location?.[key])
          .map(([, label]) => label)

        return missing.length === 0 || `Missing: ${missing.join(', ')}.${instructions ? ` ${instructions}` : ''}`
      }
    }
  },
}
